import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { OfflineBanner } from '@/components/ui/OfflineBanner';
import { SplashScreen } from '@/components/ui/SplashScreen';
import { ToastViewport } from '@/components/ui/Toast';
import { ClientLayout } from '@/components/layouts/ClientLayout';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { useOnlineStatusListener } from '@/stores/useOnlineStatus';
import { usePwaTelemetryBoot } from '@/hooks/usePwaTelemetryBoot';
import { menusApi, refreshAccessToken } from '@/lib/api';

// ── Auth ──
const Onboarding = lazy(() => import('@/pages/auth/Onboarding'));
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const RegisterConfirm = lazy(() => import('@/pages/auth/RegisterConfirm'));
const RejoindreAmbassade = lazy(() => import('@/pages/client/RejoindreAmbassade'));

// ── Client ──
const Home = lazy(() => import('@/pages/client/Home'));
const Order = lazy(() => import('@/pages/client/Order'));
const OrderSuccess = lazy(() => import('@/pages/client/OrderSuccess'));
const Orders = lazy(() => import('@/pages/client/Orders'));
const OrderDetail = lazy(() => import('@/pages/client/OrderDetail'));
const Vote = lazy(() => import('@/pages/client/Vote'));
const Credits = lazy(() => import('@/pages/client/Credits'));
const ChooseFormula = lazy(() => import('@/pages/client/ChooseFormula'));
const PaymentManual = lazy(() => import('@/pages/client/PaymentManual'));
const PaymentAuto = lazy(() => import('@/pages/client/PaymentAuto'));
const Profile = lazy(() => import('@/pages/client/Profile'));
const Distribution = lazy(() => import('@/pages/client/Distribution'));
const MonAmbassade = lazy(() => import('@/pages/client/MonAmbassade'));

// ── Admin ──
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Clients = lazy(() => import('@/pages/admin/Clients'));
const ClientDetail = lazy(() => import('@/pages/admin/ClientDetail'));
const Candidates = lazy(() => import('@/pages/admin/Candidates'));
const AdminOrders = lazy(() => import('@/pages/admin/Orders'));
const AdminPayments = lazy(() => import('@/pages/admin/Payments'));
const AdminMenus = lazy(() => import('@/pages/admin/Menus'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));
const AdminAmbassadeurs = lazy(() => import('@/pages/admin/Ambassadeurs'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RequireAuth({ admin }: { admin?: boolean }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (admin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

export default function App() {
  const initTheme = useThemeStore((s) => s.init);
  useOnlineStatusListener();
  // Baseline Installation PWA (ADR 0007 D2) : au boot, si connecté, throttlé
  // 24h, échec toujours silencieux — cf. usePwaTelemetryBoot / lib/pwa.ts.
  usePwaTelemetryBoot();

  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Bootstrap : l'accessToken vit en mémoire seule (ADR 0002 D1) et ne
  // survit pas à un refresh de page. Si un user est persisté mais qu'on n'a
  // plus d'accessToken, une seule tentative de refresh silencieuse — seule
  // une vraie invalidation serveur (401) déclenche `clear()` dans api.ts
  // (révision Frappe 1) ; un échec passager (réseau, quota) laisse l'état
  // connecté et les requêtes suivantes retenteront via l'interceptor 401.
  // La redirection vers /login vient du re-render de RequireAuth
  // (isAuthenticated devient false), pas d'un interceptor ici.
  useEffect(() => {
    const { user, accessToken } = useAuthStore.getState();
    if (user && !accessToken) {
      refreshAccessToken().catch(() => {
        /* clear() déjà fait par refreshAccessToken — rien à faire ici */
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <OfflineBanner />
      <ToastViewport />
      {!splashDone && (
        <SplashScreen
          prefetch={() => menusApi.today().catch(() => null)}
          onDone={() => setSplashDone(true)}
        />
      )}
      <Suspense fallback={<div className="p-10 text-center text-muted">Chargement…</div>}>
        <Routes>
          {/* Public */}
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/confirm" element={<RegisterConfirm />} />
          <Route path="/a/:code" element={<RejoindreAmbassade />} />

          {/* Client (auth-gated) */}
          <Route element={<RequireAuth />}>
            <Route element={<ClientLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/vote" element={<Vote />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/credits/formula" element={<ChooseFormula />} />
              <Route path="/credits/manual" element={<PaymentManual />} />
              <Route path="/credits/auto" element={<PaymentAuto />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/order" element={<Order />} />
              <Route path="/order/success" element={<OrderSuccess />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:id" element={<OrderDetail />} />
              <Route path="/distribution" element={<Distribution />} />
              <Route path="/mon-ambassade" element={<MonAmbassade />} />
            </Route>
          </Route>

          {/* Admin (auth + admin-gated) */}
          <Route element={<RequireAuth admin />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/menus" element={<AdminMenus />} />
              <Route path="/admin/candidates" element={<Candidates />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/clients" element={<Clients />} />
              <Route path="/admin/clients/:id" element={<ClientDetail />} />
              <Route path="/admin/ambassadeurs" element={<AdminAmbassadeurs />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
